import { Fragment, useEffect, useContext } from "react"

// Router
import { useParams } from "@/lib/router"

// Icons
import {
	FiDownload,
	FiPaperclip,
	FiCalendar,
	FiHash,
	FiMapPin,
	FiTrash2,
	FiEdit3,
	FiX,
} from "@/lib/icons"

// Animations
import { AnimatePresence, motion } from "@/lib/animations"

// Utils
import { transformErrorToText } from "@/api/rest/utils"
import { dateFormat } from "@/constants/constants"

// Forms
import { useFormik, FormikProvider } from "formik"
import { Yup } from "@/lib/forms"
import { FormGroup } from "@/components/form-controls/FormGroup"
import {
	FormikInput,
	FormikTextArea,
	FormikSubmitButton,
} from "@/components/form-controls/formik"
import { FormikCheckbox } from "@/components/form-controls"
import { Label } from "@/components/form-controls/Label"

// Context
import { RedirectContext } from "@/context/redirectContext"

// Hooks
import { useScrollToTop } from "@/hooks/useScrollToTop"

// State
import { useDispatch, useSelector } from "@/state/hooks"
import {
	PaymentPatchesActionType,
	handlePatchApprove,
	handlePatchClear,
	selectPatchesByInterestPaymentId,
	clearPatchChangesByInterestPaymentId,
} from "@/state/features/energySupplierApproval/paymentPatchesSlice"

// UI
import { cn } from "@/lib/utils"
import { CardWrapper, CardBody, CardFooter, Card } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { Button } from "@/components/Button"
import { useToasts } from "@/context/useToasts"
import { Tooltip } from "@/components/Tooltip"

// GraphQL
import { apiV1PaymentSupplierVerificationCreate } from "@/api/rest/generated/api/api"
import {
	PaymentStateEnum,
	usePaymentDetailEnergySupplierQuery,
} from "@/api/graphql"

// Types
import { ActionEnum, PaymentEntryCorrection } from "@/api/rest/generated/@types"

import { DateTime } from "@/lib/dates"
import { formatToDecimalPlaces } from "@/lib/math"

// Translations
import { Trans, useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Tables
import {
	PaymentOverviewForEnergySupplier,
	PaymentOverviewForEnergySupplierLoading,
} from "./PaymentOverviewForEnergySupplier"

/**
 * @returns PaymentDetailEnergySupplier
 */
export function PaymentDetailEnergySupplier() {
	// Translations
	const t = useTrans("payments")

	// Hooks
	useScrollToTop()

	// Router
	const { id: interestPaymentId } = useParams()

	// Get Data
	const { data, refetch, isLoading } = usePaymentDetailEnergySupplierQuery({
		id: interestPaymentId,
	})
	const interestPayment = data?.interest_payments?.results?.[0]

	// Context
	const { redirect } = useContext(RedirectContext)

	// Loading state
	if (isLoading) {
		return <PaymentDetailEnergySupplierLoading />
	}

	// Data state
	return (
		<div
			className="space-y-5 md:space-y-6 lg:space-y-8"
			data-id={interestPaymentId}
		>
			<Card>
				<div className="md:flex md:space-x-3">
					<Heading
						as="h2"
						styleAs="h4"
						className="mr-auto mb-3 sm:truncate"
					>
						{t("payments:copy.title")}
					</Heading>
					<div className="flex space-x-3">
						<Button
							variant="primary"
							onClick={() => {
								redirect(
									data?.interest_payments?.results?.[0]
										?.download_pdf_url ?? "",
								)
							}}
						>
							<FiDownload className="mr-2" />
							{t("payments:pdf.button")}
						</Button>
						<Button
							variant="primary"
							onClick={() => {
								redirect(
									data?.interest_payments?.results?.[0]
										?.download_csv_url ?? "",
								)
							}}
						>
							<FiDownload className="mr-2" />
							{t("payments:csv.button")}
						</Button>
					</div>
				</div>
				{t("payments:copy.supplier", {
					name: data?.me?.first_name,
					energySupplierName:
						data?.interest_payments?.results?.[0]?.project?.supplier
							?.name,
				})
					.split("\n\n")
					.map((text, index) => (
						<p
							key={`${index}.${text}`}
							className="mt-4 text-sm text-gray-700"
						>
							{text.split("\n").map((text, index) => (
								<Fragment key={`${index}.${text}`}>
									{text}
									<br />
								</Fragment>
							))}
						</p>
					))}
				<dl>
					<dt className="mt-8 text-sm font-medium text-gray-500">
						{t("payments:info_list.attachments.heading")}
					</dt>
					<dd className="mt-1 text-sm text-gray-700">
						<ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
							<DownloadPDF />
							<DownloadCSV />
						</ul>
					</dd>
				</dl>
			</Card>

			{/** Payment info list */}
			<PaymentInfoList />

			<CardWrapper data-testid="paymentoverviewforenergysupplier">
				<PaymentOverviewForEnergySupplier />
			</CardWrapper>

			{/** Show if pending supplier */}
			{interestPayment?.state === PaymentStateEnum.PendingSupplier ? (
				<PaymentDetailConfirmation refetch={refetch} />
			) : (
				<CardWrapper>
					<CardBody>
						<Heading as="h3" className="mb-3">
							{t("payments:confirmation.finished.date", {
								date: DateTime.fromISO(
									interestPayment?.energy_supplier_acceptance_date_time,
								).toFormat(dateFormat),
							})}
						</Heading>
						{t("payments:confirmation.finished.title")}
					</CardBody>
				</CardWrapper>
			)}
		</div>
	)
}

function DownloadPDF() {
	// Translations
	const t = useTrans("payments")

	// Router
	const { id: interestPaymentId } = useParams()

	// Get Data
	const { data } = usePaymentDetailEnergySupplierQuery({
		id: interestPaymentId,
	})
	if (!data?.interest_payments?.results?.[0]?.download_pdf_url) return null

	return (
		<a
			href={data.interest_payments.results[0]?.download_pdf_url}
			target="_blank"
			rel="noreferrer"
			className="group flex w-full items-center justify-between py-3 pr-4 pl-3 text-sm hover:bg-gray-100"
			data-testid="downloadpdf"
		>
			<div className="flex items-center">
				<FiPaperclip
					className="size-5 shrink-0 text-gray-400"
					aria-hidden="true"
				/>
				<span className="ml-2 flex-1 truncate">
					{t("payments:info_list.attachments.pdf.cta", {
						name: data.interest_payments.results[0]?.project?.name,
					})}
				</span>
			</div>
			<div className="text-secondary-700 ml-4 font-medium">
				{t("payments:info_list.attachments.pdf.download")}
			</div>
		</a>
	)
}

function DownloadCSV() {
	// Translations
	const t = useTrans("payments")

	// Router
	const { id: interestPaymentId } = useParams()

	// Get Data
	const { data } = usePaymentDetailEnergySupplierQuery({
		id: interestPaymentId,
	})

	if (!data?.interest_payments?.results?.[0]?.download_csv_url) return null

	return (
		<a
			href={data.interest_payments.results[0]?.download_csv_url}
			target="_blank"
			rel="noreferrer"
			className="group flex w-full items-center justify-between py-3 pr-4 pl-3 text-sm hover:bg-gray-100"
			data-testid="downloadcsv"
		>
			<div className="flex items-center">
				<FiPaperclip
					className="size-5 shrink-0 text-gray-400"
					aria-hidden="true"
				/>
				<span className="ml-2 flex-1 truncate">
					{t("payments:info_list.attachments.csv.cta", {
						name: data.interest_payments.results[0]?.project?.name,
					})}
				</span>
			</div>
			<div className="text-secondary-700 ml-4 font-medium">
				{t("payments:info_list.attachments.csv.download")}
			</div>
		</a>
	)
}

/**
 * PaymentInfoList
 * @returns
 */
function PaymentInfoList() {
	const { formatDate, formatNumber, formatCurrency } = useLang()
	const t = useTrans("payments")

	// Router
	const { id: interestPaymentId } = useParams()

	// Get Data
	const { data } = usePaymentDetailEnergySupplierQuery({
		id: interestPaymentId,
	})

	const interestPayment = data?.interest_payments?.results?.[0]

	const paymentEntries = interestPayment?.payment_entries ?? []
	const paymentEntriesWithSupplier =
		interestPayment?.payment_entries_with_supplier ?? []

	const allClientAmount = paymentEntries.length
	const withSupplierAmount = paymentEntriesWithSupplier.length

	return (
		<>
			<title>
				{t("common:web.title", {
					page: `${t("payments:title")} - ${t(
						"payments:list.interest_period.copy",
						{
							start: DateTime.fromISO(
								interestPayment?.interestperiod?.start,
							).toFormat(dateFormat),
							end: DateTime.fromISO(
								interestPayment?.interestperiod?.end,
							).toFormat(dateFormat),
						},
					)}`,
				})}
			</title>
			<CardWrapper className="bg-white">
				<CardBody className="border-b border-gray-200">
					<div className="flex justify-between">
						<Heading as="h3" className="mb-1">
							{t("payments:info_list.header", {
								name: interestPayment?.project?.name,
							})}
						</Heading>
					</div>
					<div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
						{/** Interest period date */}
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiCalendar
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							<span className="text-gray-400">
								<Trans
									ns="payments"
									i18nKey="payments:info_list.date"
									values={{
										from: DateTime.fromISO(
											interestPayment?.interestperiod
												?.start,
										).toFormat(
											t(
												"payments:info_list.date.date_format",
											),
										),
										to: DateTime.fromISO(
											interestPayment?.interestperiod
												?.end,
										).toFormat(
											t(
												"payments:info_list.date.date_format",
											),
										),
									}}
									components={{
										strong: (
											<span className="font-medium" />
										),
									}}
								/>
							</span>
						</div>
						{/** ID */}

						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiHash
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							{t("payments:info_list.transaction_id", {
								id: interestPayment?.transaction_id,
							})}
						</div>

						{/** Location */}
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<FiMapPin
								className="mr-1.5 size-5 shrink-0 text-gray-400"
								aria-hidden="true"
							/>
							{t("payments:info_list.location", {
								location: interestPayment?.project?.city,
							})}
						</div>
					</div>
				</CardBody>
				<dl>
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.tariff.title")}
						</dt>
						<dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{/* cost_kwh is in cents */}
							{interestPayment?.interestperiod?.cost_kwh
								? formatCurrency(
										interestPayment.interestperiod
											.cost_kwh / 100,
										{
											maximumFractionDigits: 4,
										},
									)
								: 0}
						</dd>
					</div>
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:info_list.rental_period_from_until.title",
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{formatDate(
								DateTime.fromISO(
									interestPayment?.interestperiod?.start,
								),
								{ variant: "verbose" },
							)}
							{" / "}
							{formatDate(
								DateTime.fromISO(
									interestPayment?.interestperiod?.end,
								),
								{ variant: "verbose" },
							)}
						</dd>
					</div>

					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.number_of_shares.title")}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{interestPayment?.project?.total_shares
								? formatNumber(
										interestPayment.project.total_shares,
									)
								: 0}
						</dd>
					</div>
					<div
						className={cn(
							"hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.client_split.title", {
								energyProvider:
									interestPayment?.project?.supplier?.name,
							})}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{t("payments:info_list.client_split.copy", {
								amount: withSupplierAmount,
								total: allClientAmount,
								percentage: formatToDecimalPlaces(
									(withSupplierAmount / allClientAmount) *
										100,
								),
							})}
						</dd>
					</div>

					{/** What zonnedelers have been generating */}
					<div
						className={cn(
							"bg-gray-50 hover:bg-gray-100",
							"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
						)}
					>
						<dt className="text-sm text-gray-500">
							{t(
								"payments:info_list.total_production_of_investors_with_supplier.title",
								{
									energySupplierName:
										interestPayment?.project?.supplier
											?.name,
								},
							)}
						</dt>
						<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
							{t(
								"payments:info_list.total_production_investor.copy",
								{
									number: interestPayment?.total_production_of_investors_with_supplier
										? formatNumber(
												parseFloat(
													interestPayment.total_production_of_investors_with_supplier,
												),
											)
										: 0,
								},
							)}
						</dd>
					</div>

					{/** What zonnedelers have been generating, LIMITED by CAP / FLOOR */}
					{interestPayment?.interest_per_share_limit_applied && (
						<div
							className={cn(
								"hover:bg-gray-100",
								"px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6",
							)}
						>
							<dt className="text-sm text-gray-500">
								{t(
									"payments:info_list.total_production_of_investors_with_supplier_limited.title",
									{
										energySupplierName:
											interestPayment?.project?.supplier
												?.name,
										interest_per_share_limit_applied:
											interestPayment?.interest_per_share_limit_applied,
									},
								)}
							</dt>
							<dd className="text-sm text-gray-700 sm:col-span-2 sm:mt-0">
								{t(
									"payments:info_list.total_production_investor.copy",
									{
										number: interestPayment?.total_production_of_investors_with_supplier_limited
											? formatNumber(
													parseFloat(
														interestPayment.total_production_of_investors_with_supplier_limited,
													),
												)
											: 0,
									},
								)}
							</dd>
						</div>
					)}
				</dl>
			</CardWrapper>
		</>
	)
}

function PaymentDetailConfirmation({ refetch }: { refetch: Function }) {
	// Translations
	const t = useTrans("payments")

	// Context
	const toasts = useToasts()

	// Router
	const { id: interestPaymentId } = useParams()

	// State
	const dispatch = useDispatch()
	const patchList = useSelector((state) =>
		selectPatchesByInterestPaymentId(state, interestPaymentId),
	)

	// Forms
	const form = useFormik({
		initialValues: {
			notes: "",
			orderNumber: "",
			status: "" as ActionEnum,
			patchList: {},
		},
		validationSchema: Yup.object().shape({
			patchList: Yup.mixed().test(
				"all-true-map",
				"All entries in the map must have the approved property set to true",
				(obj) => {
					// Check if the value is an object
					if (obj && typeof obj === "object") {
						// Convert the object values to an array and check the 'approved' property
						return Object.values(
							obj as Record<string, { approved?: boolean }>,
						).every((value) => value.approved === true)
					}
					return false // Invalid if not an object
				},
			),
		}),
		onSubmit: async (values, helpers) => {
			helpers.setStatus("")
			try {
				let correctionEntries: PaymentEntryCorrection[] = []
				Object.entries(patchList).forEach(([, value]) => {
					let paymentEntryCorrection: PaymentEntryCorrection = {
						paymententry: Number(value.paymentEntryId), // This rest API expects numbers instead of string
					}

					// Delete
					if (value.type === PaymentPatchesActionType.Delete) {
						paymentEntryCorrection.correction = "is_not_client"
					}
					if (value.type === PaymentPatchesActionType.Edit) {
						paymentEntryCorrection.correction = "is_client" // TODO: This hack is in place because the backend does not accept 'supplier_account' without the is_client value.
						paymentEntryCorrection.supplier_account = String(
							value.data?.["supplier_account"],
						)
					}

					// Push to array
					correctionEntries.push(paymentEntryCorrection)
				})

				const response = await apiV1PaymentSupplierVerificationCreate(
					Number(interestPaymentId),
					{
						action: values.status as ActionEnum,
						reference: values.orderNumber,
						message: values.notes,
						correction_entries: correctionEntries,
					},
				)

				// When payment has been confirmed
				if (response.ok === true) {
					dispatch(
						clearPatchChangesByInterestPaymentId({
							interestPaymentId: interestPaymentId,
						}),
					)
					toasts.addToast({
						variant: "success",
						id: "payment-supplier-verification-success",
						text: t("payments:confirmation.success.accept"),
					})
					refetch()
				}
			} catch (e) {
				helpers.setStatus(
					transformErrorToText(e, {
						filteredErrorMessages: new Set("insufficient_rights"),
					}),
				)
			}
		},
	})

	/**
	 * Set form value if patchList changes
	 * This is to make the validationsSchema work!
	 */
	useEffect(() => {
		void form.setFieldValue("patchList", patchList)
	}, [patchList])

	const hasErrorStatus =
		Boolean(form.status) && typeof form.status === "string"

	return (
		<FormikProvider value={form}>
			<form onSubmit={form.handleSubmit}>
				<CardWrapper>
					<CardBody>
						<Heading as="h3" className="mb-3">
							{t("payments:confirmation.title")}
						</Heading>
						<FormGroup heading={t("payments:confirmation.copy")}>
							<div className="space-y-6 sm:space-y-5">
								<div>
									<AnimatePresence initial={false}>
										{Object.keys(patchList).length > 0 && (
											<motion.div
												initial={{
													height: 0,
													opacity: 0,
												}}
												animate={{
													height: "auto",
													opacity: 1,
												}}
												exit={{ height: 0, opacity: 0 }}
												transition={{ ease: "easeOut" }}
												className="overflow-hidden"
											>
												<div className="pb-6 sm:pb-5">
													<PaymentDetailPatchList />
												</div>
											</motion.div>
										)}
									</AnimatePresence>

									{/** Order number */}
									<div>
										<Label
											className="truncate"
											htmlFor="orderNumber"
										>
											{t(
												"payments:confirmation.order_number",
											)}
										</Label>
										<FormikInput
											name="orderNumber"
											aria-label={t(
												"payments:confirmation.order_number",
											)}
											className="block w-full lg:w-3/4"
										/>
									</div>
								</div>
								<div>
									<Label className="truncate" htmlFor="notes">
										{t("payments:confirmation.note")}
									</Label>
									<FormikTextArea
										name="notes"
										rows={5}
										aria-label="notes"
										className="block w-full lg:w-1/2"
									/>
								</div>
								<div>
									<p className="text-sm text-gray-500">
										{t(
											"payments:confirmation.supplier.approve_deny.copy",
										)}
									</p>
								</div>
							</div>
						</FormGroup>
					</CardBody>
					<CardFooter className="text-right">
						<div className="flex items-center justify-end space-x-3">
							{hasErrorStatus && (
								<p className="mr-4 font-medium text-red-500">
									{t(
										`payments:confirmation.form.error.${form.status}`,
									)}
								</p>
							)}
							<Tooltip
								content={
									<p>{t("payments:confirmation.tooltip")}</p>
								}
								enabled={form.isValid === false}
							>
								<FormikSubmitButton
									onClick={() => {
										void form.setFieldValue(
											"status",
											"accept",
										)
									}}
									variant={"primary"}
									disableWhenPristine={false}
								>
									{t("payments:confirmation.form.send")}
								</FormikSubmitButton>
							</Tooltip>
						</div>
					</CardFooter>
				</CardWrapper>
			</form>
		</FormikProvider>
	)
}

/**
 * PaymentDetailPatchList
 * @returns
 */
function PaymentDetailPatchList() {
	// Translations
	const t = useTrans("payments")

	// Router
	const { id: interestPaymentId } = useParams()

	// State
	const dispatch = useDispatch()
	const patchList = useSelector((state) =>
		selectPatchesByInterestPaymentId(state, interestPaymentId),
	)

	// Get Data
	const { data } = usePaymentDetailEnergySupplierQuery({
		id: interestPaymentId,
	})

	// Data
	const patchListEntries = Object.entries(patchList)

	const deletedEntries = patchListEntries.reduce((acc, [, value]) => {
		if (value.type === "delete") return acc + 1
		return acc
	}, 0)

	const editedEntries = patchListEntries.reduce((acc, [, value]) => {
		if (value.type === "edit") return acc + 1
		return acc
	}, 0)

	const changedEntries = patchListEntries.reduce((acc, [, value]) => {
		if (value.type === "edit" && value.data)
			return acc + Object.keys(value.data).length
		return acc
	}, 0)

	function getLabelText(field: string) {
		return t(`payments:confirmation.changes.text.field.${String(field)}`)
	}

	return (
		<div className="lg:w-3/4">
			<p className="text-base font-medium text-gray-700">
				{t("payments:confirmation.changes.title")}
			</p>
			<ul className="lg:mt-2">
				<AnimatePresence initial={false}>
					{patchListEntries.map(([key, value]) => {
						// Find and display user info
						const user =
							data?.interest_payments?.results?.[0]?.payment_entries_with_supplier?.find(
								(paymentEntry) =>
									paymentEntry.id ===
									String(value.paymentEntryId),
							)

						if (!user) {
							return (
								<motion.div
									key={key}
									initial={{ height: 0 }}
									animate={{ height: "auto" }}
									exit={{ height: 0 }}
									transition={{ ease: "easeOut" }}
									className="border-b border-gray-200"
								>
									{t(
										"payments:confirmation.changes.text.error",
									)}
								</motion.div>
							)
						}

						return (
							<motion.li
								key={key}
								initial={{ height: 0, opacity: 1 }}
								animate={{ height: "auto", opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ ease: "easeOut" }}
								className="overflow-hidden border-b border-gray-200"
							>
								<div className="flex py-4">
									<div className="mr-3 flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500">
										{value.type ===
										PaymentPatchesActionType.Delete ? (
											<FiTrash2 size={14} />
										) : (
											<FiEdit3 size={14} />
										)}
									</div>
									<div>
										<div className="space-y-2">
											<p className="text-sm font-medium text-gray-900">
												{user.name}
											</p>

											{/* Delete */}
											{value.type ===
												PaymentPatchesActionType.Delete && (
												<p className="text-sm text-gray-500">
													{t(
														"payments:confirmation.changes.deleted",
													)}
												</p>
											)}

											{/* Edit */}
											{value.type ===
												PaymentPatchesActionType.Edit &&
											value.data
												? Object.entries(
														value.data,
													).map(
														([
															dataKey,
															dataValue,
														]) => (
															<p
																key={`${String(
																	dataKey,
																)}.${key}`}
																className="text-sm text-gray-500"
															>
																<Trans
																	ns="payments"
																	i18nKey="payments:confirmation.changes.text"
																	values={{
																		field: getLabelText(
																			dataKey,
																		),
																		// @ts-expect-error - TODO: fix this
																		from: user[
																			dataKey
																		],
																		to: dataValue,
																	}}
																	components={{
																		strong: (
																			<strong />
																		),
																	}}
																/>
															</p>
														),
													)
												: null}
										</div>
									</div>
									<div className="m-0 ml-auto flex items-end">
										<div className="flex size-10 cursor-pointer items-center justify-center p-0 text-sm text-gray-500 outline-0">
											<FormikCheckbox
												name={`origin.work`}
												checked={value.approved}
												onChange={() => {
													dispatch(
														handlePatchApprove({
															interestPaymentId:
																interestPaymentId,
															paymentEntryId:
																Number(
																	value.paymentEntryId,
																),
															approved:
																!value.approved,
														}),
													)
												}}
												className="order-0 mr-2"
											/>
										</div>

										<button
											className="flex size-10 cursor-pointer items-center justify-center p-0 text-sm text-gray-500 outline-0"
											onClick={(event) => {
												event.preventDefault()
												dispatch(
													handlePatchClear({
														interestPaymentId:
															interestPaymentId,
														paymentEntryId: Number(
															value.paymentEntryId,
														),
													}),
												)
											}}
										>
											<FiX className="block" />
										</button>
									</div>
								</div>
							</motion.li>
						)
					})}
				</AnimatePresence>
			</ul>
			<div className="mb-4 border-b border-gray-200 py-3 pl-11">
				{/* Deleted */}
				{deletedEntries > 0 && (
					<p className="text-sm text-gray-500">
						<Trans
							ns="payments"
							i18nKey="payments:confirmation.changes.total.deleted"
							values={{ count: deletedEntries }}
							components={{
								strong: (
									<strong className="font-medium text-gray-700" />
								),
							}}
						/>
					</p>
				)}

				{/* Edited */}
				{editedEntries > 0 ? (
					<p className="text-sm text-gray-500">
						<Trans
							ns="payments"
							i18nKey={
								editedEntries === changedEntries
									? "payments:confirmation.changes.total.edited"
									: "payments:confirmation.changes.total.edited.with_changes"
							}
							values={{
								count: editedEntries,
								text: t(
									"payments:confirmation.changes.total.changes",
									{ count: changedEntries },
								),
							}}
							components={{
								strong: (
									<strong className="font-medium text-gray-700" />
								),
							}}
						/>
					</p>
				) : null}
			</div>
		</div>
	)
}

/**
 * PaymentDetailEnergySupplierLoading
 * @returns
 */
export function PaymentDetailEnergySupplierLoading() {
	const t = useTrans("payments")

	return (
		<div className="space-y-5 md:space-y-6 lg:space-y-8">
			<Card>
				<div className="md:flex md:space-x-3">
					<Heading
						as="h2"
						styleAs="h4"
						className="mr-auto mb-3 sm:truncate"
					>
						<div className="h-6 w-48 animate-pulse rounded bg-gray-200" />
					</Heading>
					<div className="flex space-x-3">
						<div className="h-10 w-32 animate-pulse rounded bg-gray-200" />
						<div className="h-10 w-32 animate-pulse rounded bg-gray-200" />
					</div>
				</div>
				<div className="mt-4 space-y-4">
					<div className="h-4 w-full animate-pulse rounded bg-gray-200" />
					<div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
					<div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
				</div>
				<dl>
					<dt className="mt-8 text-sm font-medium text-gray-500">
						{t("payments:info_list.attachments.heading")}
					</dt>
					<dd className="mt-1">
						<ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
							<li className="group flex w-full items-center justify-between py-3 pr-4 pl-3 text-sm">
								<div className="flex items-center">
									<div className="size-5 animate-pulse rounded bg-gray-200" />
									<div className="ml-2 h-4 w-48 animate-pulse rounded bg-gray-200" />
								</div>
								<div className="ml-4 h-4 w-24 animate-pulse rounded bg-gray-200" />
							</li>
							<li className="group flex w-full items-center justify-between py-3 pr-4 pl-3 text-sm">
								<div className="flex items-center">
									<div className="size-5 animate-pulse rounded bg-gray-200" />
									<div className="ml-2 h-4 w-48 animate-pulse rounded bg-gray-200" />
								</div>
								<div className="ml-4 h-4 w-24 animate-pulse rounded bg-gray-200" />
							</li>
						</ul>
					</dd>
				</dl>
			</Card>

			<CardWrapper className="bg-white">
				<CardBody className="border-b border-gray-200">
					<div className="flex justify-between">
						<Heading as="h3" className="mb-1">
							<div className="h-6 w-64 animate-pulse rounded bg-gray-200" />
						</Heading>
					</div>
					<div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-8">
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
						</div>
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
						</div>
						<div className="mt-2 flex items-center text-sm text-gray-500">
							<div className="mr-1.5 size-5 animate-pulse rounded bg-gray-200" />
							<div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
						</div>
					</div>
				</CardBody>
				<dl>
					<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.tariff.title")}
						</dt>
						<dd className="mt-1 sm:col-span-2 sm:mt-0">
							<div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
					<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t(
								"payments:info_list.rental_period_from_until.title",
							)}
						</dt>
						<dd className="sm:col-span-2 sm:mt-0">
							<div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
					<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.number_of_shares.title")}
						</dt>
						<dd className="sm:col-span-2 sm:mt-0">
							<div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
					<div className="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
						<dt className="text-sm text-gray-500">
							{t("payments:info_list.total_production.title")}
						</dt>
						<dd className="sm:col-span-2 sm:mt-0">
							<div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
						</dd>
					</div>
				</dl>
			</CardWrapper>

			<CardWrapper data-testid="paymentoverviewforenergysupplier">
				<PaymentOverviewForEnergySupplierLoading />
			</CardWrapper>

			<CardWrapper>
				<CardBody>
					<Heading as="h3" className="mb-3">
						<div className="h-6 w-64 animate-pulse rounded bg-gray-200" />
					</Heading>
					<div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
				</CardBody>
			</CardWrapper>
		</div>
	)
}
